import { flexRender, Header } from "@tanstack/react-table";
import { Property } from "../../../rest/responses/Property";
import { useSortable } from "@dnd-kit/sortable";
import { CSSProperties } from "react";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "semantic-ui-react";
import clsx from "clsx";
import "./DraggableTableHeader.scss";
import { useDataTableContext } from "../../../contexts/DataTableContext";
import { Accessor } from "../../../types/types";
import ColumnResizeHandle from "./ColumnResizeHandle";

const DraggableTableHeader = ({ header }: { header: Header<Property, unknown> }) => {
	const { selectedIds, onBulkEdit, getFriendlyColumnName } = useDataTableContext()!;
	const enableBulkEdit = !!onBulkEdit;

	const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
		id: header.column.id,
	});

	const isDraggable = !header.column.columnDef.meta?.isFixed;

	let style: CSSProperties = {
		position: "relative",
		transition: "width transform 0.2s ease-in-out",
		whiteSpace: "nowrap",
		display: "flex",
		width: header.getSize(),
		zIndex: 0,
	};

	if (isDraggable) {
		style = Object.assign(style, {
			opacity: isDragging ? 0.8 : 1,
			transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
			zIndex: isDragging ? 1 : 0,
		});
	}

	const isSortable = header.column.getCanSort();
	const isSorted = header.column.getIsSorted();
	const isBulkEditable = enableBulkEdit && header.column.columnDef.meta?.enableBulkEdit !== false;
	const title = getFriendlyColumnName(header.column.id as Accessor);

	return (
		<th
			className={clsx(isDraggable && "draggable", isDragging && "dragging", isSorted && "sorted")}
			colSpan={header.colSpan}
			ref={isDraggable ? setNodeRef : undefined}
			style={style}
		>
			<span className="inner">
				<span
					className={clsx("name", isSortable && "sortable")}
					title={title}
					onClick={header.column.getToggleSortingHandler()}
				>
					{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
				</span>
				<span>
					{isSortable &&
						({
							asc: (
								<Icon
									name="sort up"
									title="Sorted ascending"
									onClick={header.column.getToggleSortingHandler()}
								/>
							),
							desc: (
								<Icon
									name="sort down"
									title="Sorted descending"
									onClick={header.column.getToggleSortingHandler()}
								/>
							),
						}[header.column.getIsSorted() as string] ?? (
							<Icon name="sort" title="Toggle sort" onClick={header.column.getToggleSortingHandler()} />
						))}
					{isBulkEditable && (
						<Icon
							name="edit"
							disabled={selectedIds.length === 0}
							onClick={() => onBulkEdit(header.column.id as Accessor)}
						/>
					)}
					{!header.column.columnDef.meta?.isFixed && (
						<button {...attributes} {...listeners} title="Drag to move column">
							<Icon name="bars" />
						</button>
					)}
				</span>
			</span>
			<ColumnResizeHandle header={header} />
		</th>
	);
};

export default DraggableTableHeader;
